import { useEffect, useState } from "react";
import Container from "@/components/Container";
import Pager, { usePager } from "@/components/Pager";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Trash2, ToggleLeft, ToggleRight } from "lucide-react";

const TYPES = ["portal","auction","agent","council","open_data","csv","manual","partner_lead","direct_seller_lead","google_query"];

export default function SourceManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", source_type: "portal", country: "United Kingdom", website_url: "", category: "" });
  const load = () => api.get("/sources").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Name required");
    await api.post("/sources", form);
    setForm({ name: "", source_type: "portal", country: "United Kingdom", website_url: "", category: "" });
    toast.success("Source added"); load();
  };
  const toggle = async (s) => { await api.put(`/sources/${s.id}`, { active: !s.active }); load(); };
  const del = async (id) => { if (!window.confirm("Delete source?")) return; await api.delete(`/sources/${id}`); toast.success("Deleted"); load(); };

  const pager = usePager(items, 12);

  return (
    <Container className="py-8 md:py-12">
      <p className="overline">Discovery</p>
      <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Source Manager</h1>
      <p className="text-ivory/50 mt-2 text-sm">Manage portals, auction houses, agents and councils we monitor.</p>

      <form onSubmit={add} className="luxury-card p-5 mt-8 grid grid-cols-1 md:grid-cols-6 gap-3">
        <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="source-name" />
        <select className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" value={form.source_type} onChange={e => setForm({ ...form, source_type: e.target.value })}>
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="URL" value={form.website_url} onChange={e => setForm({ ...form, website_url: e.target.value })} />
        <button className="btn-ruby justify-center" data-testid={ADMIN.addSource}>Add Source</button>
      </form>

      <div className="mt-6 luxury-card divide-y divide-charcoal-600/60">
        {items.length === 0 && <p className="p-8 text-center text-ivory/40 text-sm">No sources.</p>}
        {pager.pageItems.map(s => (
          <div key={s.id} className="p-4 grid grid-cols-1 md:grid-cols-12 items-center gap-3" data-testid={`source-row-${s.id}`}>
            <div className="md:col-span-3">
              <p className="text-ivory">{s.name}</p>
              <p className="text-[0.65rem] uppercase tracking-[0.15em] text-ivory/40">{s.source_type}</p>
            </div>
            <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="md:col-span-4 text-xs text-gold/80 hover:text-gold truncate">{s.website_url}</a>
            <p className="md:col-span-2 text-xs text-ivory/50">{s.category}</p>
            <p className="md:col-span-1 text-xs text-ivory/50">{s.country}</p>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button onClick={() => toggle(s)} className="btn-outline !py-1 !px-2">
                {s.active ? <ToggleRight size={14} className="text-gold" /> : <ToggleLeft size={14} />}
              </button>
              <button onClick={() => del(s.id)} className="btn-outline !py-1 !px-2"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      <Pager {...pager} testid="sources-pager" />
    </Container>
  );
}
